import apiClient from './client';

// Group member with details
export interface GroupMember {
  user_id: string;
  first_name: string;
  last_name: string;
  // MED-1162 — preferred name when set; the session-leader picker shows the
  // full formal name with a "(preferred)" parenthetical (AC5).
  preferred_name: string | null;
  email: string;
  role: string | null;
  joined_at: string;
}

// Group with stats
export interface Group {
  id: string;
  cohort_id: string;
  name: string;
  description: string | null;
  max_size: number;
  coach_id: string | null;
  coach_name?: string;
  coach_email?: string;
  coach_avatar_url?: string;
  is_active: boolean;
  member_count: number;
  avg_engagement: number | null;
  // MED-1266: canonical server-computed group attendance + marking coverage (AC1/AC9)
  attendance_percentage: number | null;
  attendance_marked: number;
  attendance_population: number;
  // Time slot schedule info
  scheduled_time_slot_id: string | null;
  time_slot_day?: string;
  time_slot_time?: string;
  time_slot_timezone?: string;
  time_slot_identifier?: string;
  // Non-null when the group was last written by a matching-server leg (E) callback (E23-2)
  synced_by_matching_session_id: string | null;
  created_at: string;
  updated_at: string;
}

// Group with members
export interface GroupWithMembers extends Group {
  members: GroupMember[];
}

// Create group request
export interface CreateGroupRequest {
  name: string;
  description?: string;
  maxSize?: number;
  coachId?: string;
}

// Update group request
export interface UpdateGroupRequest {
  name?: string;
  description?: string;
  maxSize?: number;
  coachId?: string | null;
  isActive?: boolean;
  timeSlotIdentifier?: string | null; // For assigning a time slot to the group
}

// Import preview participant
export interface GroupImportPreviewParticipant {
  email: string;
  firstName: string;
  lastName: string;
  role: string | null;
  groupNumber: number | null;
  matched: boolean;
  alreadyAssigned: boolean;
  currentGroupName?: string;
  availableTimeSlots?: string[];
}

// Import preview group
export interface GroupImportPreviewGroup {
  groupNumber: number;
  name: string;
  participants: GroupImportPreviewParticipant[];
  scheduledTimeSlot?: string;
  availableTimeSlots?: string[];
}

// Import preview response
export interface GroupImportPreviewResponse {
  groups: GroupImportPreviewGroup[];
  unassigned: GroupImportPreviewParticipant[];
  summary: {
    totalGroups: number;
    totalMatched: number;
    totalUnmatched: number;
    totalAlreadyAssigned: number;
  };
  timeSlotInfo?: {
    hasTimeSlotData: boolean;
    validTimeSlots: string[];
    invalidTimeSlots: string[];
  };
  /**
   * MED-1290: why rows landed in `unassigned`. Absent when every row had a
   * usable group number. Present-but-unreadable values are reported apart from
   * genuinely empty ones — repairing the file and hand-assigning are different
   * fixes, and the CM couldn't previously tell which was needed.
   */
  groupColumnInfo?: {
    unreadableValues: string[];
    unreadableCount: number;
    emptyCount: number;
  };
}

// Import preview request
export interface GroupImportPreviewRequest {
  csvData: string[][];
  emailColumnIndex: number;
  groupColumnIndex: number;
  scheduledTimeSlotColumnIndex?: number;
  availableTimeSlotsColumnIndex?: number;
}

// Import execute request
export interface GroupImportExecuteRequest {
  groups: Array<{
    name: string;
    memberEmails: string[];
    scheduledTimeSlot?: string;
    availableTimeSlots?: string[];
  }>;
}

// Pagination metadata for groups list
export interface GroupsPagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// Groups list response (paginated)
export interface GroupsListResponse {
  data: GroupWithMembers[];
  pagination: GroupsPagination;
}

// Groups list params
export interface GroupsListParams {
  limit?: number;
  offset?: number;
  search?: string;
}

// Import result
export interface GroupImportResult {
  groupsCreated: number;
  participantsAssigned: number;
  groups: Array<{
    id: string;
    name: string;
    memberCount: number;
  }>;
  unassignedEmails: string[];
}

// Invite lock status for a group (MED-648)
export interface GroupInviteStatus {
  totalFutureSessions: number;
  invitedSessions: number;
  uninvitedSessions: number;
  isFullyLocked: boolean;
}

export const groupsApi = {
  /**
   * Get groups for a cohort with their members (paginated)
   */
  list: async (cohortId: string, params?: GroupsListParams): Promise<GroupsListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));
    if (params?.search) searchParams.set('search', params.search);
    const qs = searchParams.toString();
    const url = `/api/cohorts/${cohortId}/groups${qs ? `?${qs}` : ''}`;
    return apiClient.get<GroupsListResponse>(url);
  },

  /**
   * Get a single group by ID with members
   */
  getById: async (cohortId: string, groupId: string): Promise<GroupWithMembers> => {
    return apiClient.get<GroupWithMembers>(`/api/cohorts/${cohortId}/groups/${groupId}`);
  },

  /**
   * Create a new group
   */
  create: async (cohortId: string, data: CreateGroupRequest): Promise<Group> => {
    return apiClient.post<Group>(`/api/cohorts/${cohortId}/groups`, data);
  },

  /**
   * Update a group
   */
  update: async (cohortId: string, groupId: string, data: UpdateGroupRequest): Promise<Group> => {
    return apiClient.put<Group>(`/api/cohorts/${cohortId}/groups/${groupId}`, data);
  },

  /**
   * Delete a group
   */
  delete: async (cohortId: string, groupId: string): Promise<void> => {
    return apiClient.delete<void>(`/api/cohorts/${cohortId}/groups/${groupId}`);
  },

  /**
   * Assign a participant to a group
   */
  assignMember: async (cohortId: string, groupId: string, userId: string): Promise<void> => {
    return apiClient.post<void>(`/api/cohorts/${cohortId}/groups/${groupId}/members`, { userId });
  },

  /**
   * Remove a participant from a group
   */
  removeMember: async (cohortId: string, groupId: string, userId: string): Promise<void> => {
    return apiClient.delete<void>(`/api/cohorts/${cohortId}/groups/${groupId}/members/${userId}`);
  },

  /**
   * Get invite lock status for a group (MED-648)
   */
  getInviteStatus: async (cohortId: string, groupId: string): Promise<GroupInviteStatus> => {
    return apiClient.get<GroupInviteStatus>(`/api/cohorts/${cohortId}/groups/${groupId}/invite-status`);
  },

  /**
   * Preview bulk group import from CSV
   */
  previewImport: async (cohortId: string, data: GroupImportPreviewRequest): Promise<GroupImportPreviewResponse> => {
    return apiClient.post<GroupImportPreviewResponse>(`/api/cohorts/${cohortId}/groups/import/preview`, data);
  },

  /**
   * Execute bulk group import
   */
  executeImport: async (cohortId: string, data: GroupImportExecuteRequest): Promise<GroupImportResult> => {
    return apiClient.post<GroupImportResult>(`/api/cohorts/${cohortId}/groups/import`, data);
  },
};

export default groupsApi;
